import axios, { AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios'
import toast from 'react-hot-toast'
// 移除直接导入 useAuthStore，改为动态导入

// 错误信息映射表（支持多语言扩展）
const errorMessages: Record<string, string> = {
  // 认证相关错误
  'AUTH_001': '用户名或密码错误',
  'AUTH_002': '用户账户已被禁用',
  'AUTH_003': '认证失败',
  'AUTH_004': '登录凭证已过期',
  'AUTH_005': '无效的刷新令牌',
  
  // 授权相关错误
  'AUTHZ_001': '权限不足',
  
  // 资源相关错误
  'RESOURCE_001': '资源未找到',
  
  // 数据验证错误
  'VALIDATION_001': '数据验证失败',
  'VALIDATION_002': '请求参数验证失败',
  'VALIDATION_003': '用户名已存在',
  'VALIDATION_004': '邮箱已被注册',
  
  // 业务逻辑错误
  'BUSINESS_001': '业务逻辑错误',
  
  // 系统错误
  'SYSTEM_001': '服务器内部错误',
  'SYSTEM_002': '数据库操作失败',
  'SYSTEM_003': '外部服务调用失败',
}

// 创建 axios 实例
const http = axios.create({
  baseURL: '/api/v1',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// 请求拦截器 - 增强版，添加详细日志记录
http.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // 动态导入 useAuthStore 以避免循环依赖
    const { useAuthStore } = await import('@/store/auth')
    // 添加认证 token
    const { token } = useAuthStore.getState()
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // 对短信登录请求添加详细日志
    if (config.url && config.url.includes('phone-login') && config.method === 'post') {
      console.log('HTTP拦截器: 短信登录请求');
      console.log('HTTP拦截器: URL:', config.url);
      console.log('HTTP拦截器: 请求体类型:', typeof config.data);
      console.log('HTTP拦截器: 请求体包含sms_code:', config.data && 'sms_code' in config.data);
      console.log('HTTP拦截器: 请求体完整内容:', config.data);
      console.log('HTTP拦截器: 请求头:', config.headers);
      
      // 序列化请求体进行记录（如果存在）
      if (config.data) {
        try {
          const serializedData = JSON.stringify(config.data);
          console.log('HTTP拦截器: 序列化后的请求体:', serializedData);
        } catch (e) {
          console.error('HTTP拦截器: 请求体序列化失败:', e);
        }
      }
    }
    
    // 对发送验证码请求添加详细日志
    if (config.url && config.url.includes('sms/send') && config.method === 'post') {
      console.log('HTTP拦截器: 发送验证码请求');
      console.log('HTTP拦截器: URL:', config.url);
      console.log('HTTP拦截器: 请求体类型:', typeof config.data);
      console.log('HTTP拦截器: 请求体包含phone:', config.data && 'phone' in config.data);
      console.log('HTTP拦截器: 请求体包含purpose:', config.data && 'purpose' in config.data);
      console.log('HTTP拦截器: 请求体完整内容:', config.data);
      console.log('HTTP拦截器: 请求头:', config.headers);
      
      // 序列化请求体进行记录（如果存在）
      if (config.data) {
        try {
          const serializedData = JSON.stringify(config.data);
          console.log('HTTP拦截器: 序列化后的请求体:', serializedData);
        } catch (e) {
          console.error('HTTP拦截器: 请求体序列化失败:', e);
        }
      }
    }
    
    return config
  },
  (error: AxiosError) => {
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
http.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error: AxiosError) => {
    const { response, config } = error
    
    try {
      // 解析错误信息，支持后端统一错误格式
      let errorCode = 'UNKNOWN'
      let errorMessage = '请求失败'
      let errorDetails: any = {}
      
      if (response?.data) {
        // 检查是否是后端返回的统一错误格式
        if (typeof response.data === 'object' && 'code' in response.data) {
          errorCode = (response.data as any).code || 'UNKNOWN'
          errorMessage = (response.data as any).message || errorMessages[errorCode] || '请求失败'
          errorDetails = (response.data as any).details || {}
        } else if ((response.data as any)?.message) {
          // 兼容旧格式
          errorMessage = (response.data as any).message
        }
      } else {
        errorMessage = error.message || '网络错误'
      }
      
      // 处理 401 未授权错误
      if (response?.status === 401) {
        // 动态导入 useAuthStore 以避免循环依赖
        const { useAuthStore } = await import('@/store/auth')
        const { refreshToken, logout, refreshAuthToken } = useAuthStore.getState()
        
        // 如果是刷新令牌的请求失败，直接登出
        if (config?.url?.includes('/auth/refresh')) {
          logout()
          toast.error(errorMessage)
          // 开发环境下不强制跳转，避免预览中断
          if (!import.meta.env.DEV) {
            window.location.href = '/auth/login'
          } else {
            console.warn('HTTP: 401 错误，开发环境下跳过强制登录跳转');
          }
          return Promise.reject({ code: errorCode, message: errorMessage, details: errorDetails })
        }
        
        // 尝试刷新令牌
        if (refreshToken && config) {
          try {
            await refreshAuthToken()
            
            // 重新发送原请求
            const { token } = useAuthStore.getState()
            if (token && config.headers) {
              config.headers.Authorization = `Bearer ${token}`
            }
            
            return http.request(config)
          } catch (refreshError) {
            logout()
            toast.error(errorMessage)
            if (!import.meta.env.DEV) {
              window.location.href = '/auth/login'
            } else {
              console.warn('HTTP: 刷新 Token 失败，开发环境下跳过强制登录跳转');
            }
            return Promise.reject({ code: errorCode, message: errorMessage, details: errorDetails })
          }
        } else {
          logout()
          toast.error(errorMessage)
          if (!import.meta.env.DEV) {
            window.location.href = '/auth/login'
          } else {
            console.warn('HTTP: 未找到 Refresh Token，开发环境下跳过强制登录跳转');
          }
        }
      } else if (response?.status === 403) {
        // 处理 403 权限不足错误
        toast.error(errorMessage)
        // 可以添加跳转到无权限页面的逻辑
      } else if (response?.status === 404) {
        // 处理 404 资源未找到错误
        toast.error(errorMessage)
        // 可以添加跳转到 404 页面的逻辑
      } else if (response?.status === 422) {
        // 处理 422 数据验证错误
        // 如果有详细的验证错误信息，可以特殊处理
        if (errorDetails.validation_errors && Array.isArray(errorDetails.validation_errors)) {
          const firstError = errorDetails.validation_errors[0]
          const validationMessage = firstError ? `${firstError.field || ''}: ${firstError.message || ''}` : errorMessage
          toast.error(validationMessage.trim().replace(/^:/, ''))
        } else {
          toast.error(errorMessage)
        }
      } else if (response && response.status >= 500) {
        // 处理 500+ 服务器错误
        // 搜索接口不显示toast，由页面组件处理错误状态
        if (!config?.url?.includes('/search/search')) {
          toast.error(errorMessage)
        }
      } else {
        // 处理其他错误
        toast.error(errorMessage)
      }
      
      // 只在非取消请求时显示错误提示
      if (!axios.isCancel(error)) {
        // 已经在上面的各种情况中处理了 toast 提示
      }
      
      // 返回标准化的错误对象
      const standardizedError = {
        code: errorCode,
        message: errorMessage,
        details: errorDetails,
        originalError: error
      }
      
      return Promise.reject(standardizedError)
    } catch (handlerError) {
      console.error('全局错误处理器异常:', handlerError)
      // 确保即使处理器本身出错也能向用户显示错误
      toast.error('处理请求错误时发生异常')
      return Promise.reject(error)
    }
  }
)

export default http